/**
 * Custom heightmap downsampling for THREE.js.
 * @module
 */
import * as THREE from "three";
import { FullScreenQuad } from "../../threejs/examples/jsm/postprocessing/FullScreenQuad";
import { DepthDownsampleShader } from "../../threejs/higharc/DepthDownsampleShader";

function initialMipLevel(size: number): HTMLCanvasElement {
  // We have to create these canvas elements since three.js requires some kind of image data to initialize mip levels.
  const imageCanvas = document.createElement("canvas");
  imageCanvas.width = imageCanvas.height = size;
  return imageCanvas;
}

const scratchRenderTargets: THREE.WebGLRenderTarget[] = []; // Sorted from smallest to largest, first one 1x1 pixels.

export function generateHierarchicalDepthMap(
  renderer: THREE.WebGLRenderer,
  srcRenderTarget: THREE.WebGLRenderTarget,
): THREE.Texture {
  if (!THREE.MathUtils.isPowerOfTwo(srcRenderTarget.width) || !THREE.MathUtils.isPowerOfTwo(srcRenderTarget.height)) {
    throw "NPOT textures are not supported in generateHierarchicalDepthMap";
  }

  if (srcRenderTarget.width !== srcRenderTarget.height) {
    throw "Non-square textures are not supported in generateHierarchicalDepthMap";
  }

  const mipLevels = Math.floor(Math.log2(Math.max(srcRenderTarget.width, srcRenderTarget.height))) + 1;

  // Make sure that reusable temporary render targets are initialized.
  if (
    (scratchRenderTargets.length > 0 && scratchRenderTargets[0].texture.format !== srcRenderTarget.texture.format) ||
    scratchRenderTargets[0].texture.type !== srcRenderTarget.texture.type
  ) {
    for (const renderTarget of scratchRenderTargets) {
      renderTarget.dispose();
    }
    scratchRenderTargets.splice(0, scratchRenderTargets.length);
  }
  for (let i = scratchRenderTargets.length; i < mipLevels - 1; ++i) {
    const tmpWidth = 1 << i;
    scratchRenderTargets[i] = new THREE.WebGLRenderTarget(tmpWidth, tmpWidth, {
      format: srcRenderTarget.texture.format as THREE.PixelFormat,
      type: srcRenderTarget.texture.type,
      colorSpace: THREE.NoColorSpace,
      depthBuffer: false,
    });
  }

  const mipLevel0 = initialMipLevel(srcRenderTarget.width);
  const dstTexture = new THREE.CanvasTexture(
    mipLevel0,
    THREE.UVMapping,
    THREE.RepeatWrapping,
    THREE.RepeatWrapping,
    THREE.NearestFilter,
    THREE.LinearMipmapNearestFilter,
    THREE.RedFormat,
    THREE.UnsignedByteType,
    0,
  );
  dstTexture.mipmaps[0] = mipLevel0;
  let mipWidth = srcRenderTarget.width;
  for (let i = 1; i < mipLevels; ++i) {
    mipWidth *= 0.5;
    dstTexture.mipmaps[i] = initialMipLevel(mipWidth);
  }
  if (!(mipWidth === 1)) {
    console.assert(false);
  }
  dstTexture.colorSpace = THREE.NoColorSpace;
  dstTexture.needsUpdate = true;

  const downsampleMaterial = new THREE.ShaderMaterial({
    defines: {
      DEPTH_PACKING: 0,
      DST_DEPTH_PACKING: 0,
      MIN_FILTER: 0,
      MAX_FILTER: 1,
      DEPTH_IN_ALPHA: 0,
    },
    uniforms: THREE.UniformsUtils.clone(DepthDownsampleShader.uniforms),
    vertexShader: DepthDownsampleShader.vertexShader,
    fragmentShader: DepthDownsampleShader.fragmentShader,
    blending: THREE.NoBlending,
  });

  const fsQuad = new FullScreenQuad(downsampleMaterial);

  renderer.setRenderTarget(srcRenderTarget, undefined, 0);

  renderer.copyFramebufferToTexture(dstTexture, new THREE.Vector2(0, 0), 0);

  let srcMipWidth = srcRenderTarget.width;

  for (let dstMipLevel = 1; dstMipLevel < mipLevels; ++dstMipLevel) {
    const scratchTargetIndex = mipLevels - 1 - dstMipLevel;
    renderer.setRenderTarget(scratchRenderTargets[scratchTargetIndex]);
    if (dstMipLevel > 1) {
      downsampleMaterial.uniforms.tDepth.value = scratchRenderTargets[scratchTargetIndex + 1].texture;
    } else {
      downsampleMaterial.uniforms.tDepth.value = srcRenderTarget.texture;
    }
    downsampleMaterial.uniforms.invSize.value.x = 1 / srcMipWidth;
    downsampleMaterial.uniforms.invSize.value.y = 1 / srcMipWidth;
    renderer.setClearColor(0x000000);
    renderer.clear(true);
    fsQuad.render(renderer);

    renderer.copyFramebufferToTexture(dstTexture, new THREE.Vector2(0, 0), dstMipLevel);

    srcMipWidth = srcMipWidth * 0.5;
  }

  renderer.setRenderTarget(null);

  return dstTexture;
}
